import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { materialsAPI } from '@/utils/api';
import { Plus, Edit, Trash2, AlertTriangle, Crown, Search, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Materials = () => {
  const [materials, setMaterials] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [alertsCollapsed, setAlertsCollapsed] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [lockedIds, setLockedIds] = useState([]);
  const [formData, setFormData] = useState({
    name: '', category: 'Huile', supplier: '', purchase_price_ttc: '', 
    purchase_quantity: '', unit: 'g', stock_quantity: '', min_stock_alert: ''
  });

  const categories = ['Huile', 'Beurre', 'Actif', 'Fragrance', 'Contenant', 'Packaging'];
  const units = ['g', 'ml', 'unité'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const [matsRes, alertsRes, planRes] = await Promise.all([
        materialsAPI.getAll(user.id || 'demo-user'),
        materialsAPI.getLowStockAlerts(user.id || 'demo-user'),
        fetch(`${API_BASE}/api/user/plan?user_id=${user.id || 'demo-user'}`).then(r => r.json())
      ]);
      setMaterials(matsRes.data);
      setAlerts(alertsRes.data.materials || []);
      setLockedIds(planRes.locked_material_ids || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!editingId) {
        const checkRes = await fetch(`${API_BASE}/api/user/can-create?resource=raw_material&user_id=${user.id || 'demo-user'}`);
        const checkData = await checkRes.json();
        if (!checkData.allowed) {
          toast.error(`Limite atteinte (${checkData.current}/${checkData.limit} matières). Passez au plan Premium pour continuer.`, { duration: 5000 });
          return;
        }
      }
      const data = {
        ...formData,
        purchase_price_ttc: parseFloat(formData.purchase_price_ttc),
        purchase_quantity: parseFloat(formData.purchase_quantity),
        stock_quantity: parseFloat(formData.stock_quantity || 0),
        min_stock_alert: parseFloat(formData.min_stock_alert || 0)
      };

      if (editingId) {
        await materialsAPI.update(editingId, data, user.id || 'demo-user');
        toast.success('Matière mise à jour');
      } else {
        await materialsAPI.create(data, user.id || 'demo-user');
        toast.success('Matière ajoutée');
      }

      setDialogOpen(false);
      setEditingId(null);
      setFormData({
        name: '', category: 'Huile', supplier: '', purchase_price_ttc: '', 
        purchase_quantity: '', unit: 'g', stock_quantity: '', min_stock_alert: ''
      });
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur');
    }
  };

  const handleEdit = (material) => {
    setEditingId(material.id);
    setFormData({
      name: material.name,
      category: material.category,
      supplier: material.supplier,
      purchase_price_ttc: material.purchase_price_ttc,
      purchase_quantity: material.purchase_quantity,
      unit: material.unit,
      stock_quantity: material.stock_quantity,
      min_stock_alert: material.min_stock_alert
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette matière ?')) return;
    try {
      await materialsAPI.delete(id);
      toast.success('Supprimée');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) return <div className="p-8"><div className="skeleton-shimmer h-64 rounded-lg"></div></div>;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-semibold mb-2">Matières Premières</h1>
          <p className="text-muted-foreground">Gestion du stock et des prix</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="search-materials"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-56"
            />
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-new-material" onClick={() => { setEditingId(null); setFormData({ name: '', category: 'Huile', supplier: '', purchase_price_ttc: '', purchase_quantity: '', unit: 'g', stock_quantity: '', min_stock_alert: '' }); }}>
              <Plus size={18} className="mr-2" /> Nouvelle Matière
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Modifier' : 'Nouvelle'} Matière Première</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Nom</label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Catégorie</label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Fournisseur</label>
                  <Input value={formData.supplier} onChange={(e) => setFormData({...formData, supplier: e.target.value})} required />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Prix Achat TTC (€)</label>
                  <Input type="number" step="0.01" value={formData.purchase_price_ttc} onChange={(e) => setFormData({...formData, purchase_price_ttc: e.target.value})} required />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Quantité Achat</label>
                  <Input type="number" step="0.01" value={formData.purchase_quantity} onChange={(e) => setFormData({...formData, purchase_quantity: e.target.value})} required />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Unité</label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2" value={formData.unit} onChange={(e) => setFormData({...formData, unit: e.target.value})}>
                    {units.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Stock Actuel</label>
                  <Input type="number" step="0.01" value={formData.stock_quantity} onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Alerte Stock Minimum</label>
                  <Input type="number" step="0.01" value={formData.min_stock_alert} onChange={(e) => setFormData({...formData, min_stock_alert: e.target.value})} />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Annuler</Button>
                <Button type="submit">{editingId ? 'Mettre à jour' : 'Créer'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {alerts.length > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardHeader className="py-3 cursor-pointer" onClick={() => setAlertsCollapsed(!alertsCollapsed)}>
            <CardTitle className="text-amber-800 flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <AlertTriangle size={16} /> {alerts.length} Alerte(s) Stock Bas
              </span>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-amber-700 hover:text-amber-900 hover:bg-amber-100">
                {alertsCollapsed ? 'Voir' : 'Réduire'}
              </Button>
            </CardTitle>
          </CardHeader>
          {!alertsCollapsed && (
            <CardContent className="pt-0">
              <div className="space-y-2">
                {alerts.map(m => (
                  <div key={m.id} className="text-sm text-amber-700">
                    <strong>{m.name}</strong>: {m.stock_quantity} {m.unit} (min: {m.min_stock_alert})
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      <Tabs defaultValue="ingredients" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="ingredients">Ingrédients</TabsTrigger>
          <TabsTrigger value="containers">Contenants</TabsTrigger>
          <TabsTrigger value="packaging">Étiquettes & Emballage</TabsTrigger>
        </TabsList>

        <TabsContent value="ingredients">
          {renderMaterialsList(['Huile', 'Beurre', 'Actif', 'Fragrance', 'Ingrédient'])}
        </TabsContent>

        <TabsContent value="containers">
          {renderMaterialsList(['Contenant'])}
        </TabsContent>

        <TabsContent value="packaging">
          {renderMaterialsList(['Packaging'])}
        </TabsContent>
      </Tabs>
    </div>
  );

  function renderMaterialsList(categoriesFilter) {
    const filteredMaterials = materials.filter(m => 
      categoriesFilter.includes(m.category) && 
      m.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (filteredMaterials.length === 0) {
      return (
        <Card className="p-12">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">Aucune matière dans cette catégorie</p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus size={18} className="mr-2" /> Ajouter
            </Button>
          </div>
        </Card>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-4">
        {filteredMaterials.map(m => {
          const isLocked = lockedIds.includes(m.id);
          return (
          <Card key={m.id} className={`transition-all ${isLocked ? 'opacity-50 border-gray-300' : 'hover:border-primary/20'}`}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    {isLocked && <Lock size={16} className="text-gray-500" />}
                    <h3 className="text-lg font-semibold">{m.name}</h3>
                    <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">{m.category}</span>
                    {m.stock_quantity <= m.min_stock_alert && !isLocked && (
                      <AlertTriangle size={16} className="text-amber-600" />
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Fournisseur</p>
                      <p className="font-medium">{m.supplier}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Prix Achat</p>
                      <p className="font-medium">{m.purchase_price_ttc}€ / {m.purchase_quantity}{m.unit}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Coût Unitaire</p>
                      <p className="font-medium text-green-600">{m.unit_cost.toFixed(4)}€/{m.unit}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Stock</p>
                      <p className="font-medium">{m.stock_quantity} {m.unit}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {isLocked ? (
                    <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded flex items-center gap-1"><Lock size={12} /> Verrouillé</span>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => handleEdit(m)}><Edit size={16} /></Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(m.id)}><Trash2 size={16} /></Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>
    );
  }
};

export default Materials;
